import { Component, OnInit, ViewChild } from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { ActivatedRoute } from '@angular/router';
import { ADPResourceService } from 'digital-core';
import { AccountService } from 'src/app/core/services/account.service';
import { PghUserDeviceBase } from 'src/app/shared/device/pgh-user-device';
import { ImeiLookupDeviceTemplate, ImeiResult } from './components/imei-lookup/imei-lookup.component';
import { ImeiInfoDeviceType } from './components/imei-info/imei-info.component';
import { NextStepsInfoDeviceTemplate } from './components/next-steps/next-steps.component';
import { UpdateDeviceBodyDto } from 'src/app/core/services/device-inventory/update-device-body.dto';
import { DeviceInfoFormResult } from './components/cellular-device-info-form/cellular-device-info-form.component';
import { UtilityService } from 'src/app/core/services/utility.service';
import { DeviceInventoryService } from 'src/app/core/services/device-inventory.service';
import { HttpErrorResponse } from '@angular/common/http';
import { GlobalModalWindowService } from 'src/app/core/services/global-modal-window.service';

@Component({
  selector: 'pgh-phone-diagnostics',
  templateUrl: './phone-diagnostics.component.html',
  styleUrls: ['./phone-diagnostics.component.scss']
})
export class PhoneDiagnosticsComponent implements OnInit {
  states = {
    LandingPage: 1,
    NextSteps: 2,
    ConfirmNumber: 3,
    ImeiInfo: 4,
    ProvideAdditionalInfo: 5
  }

  @ViewChild('imeisidenav') sidenav: MatSidenav;
  events: string[] = [];
  opened: boolean;
  phoneNumber: string;
  imeiStepResult: ImeiResult;
  isBackVisible: boolean = false;

  invitationCode: string;
  hylaLink: string;
  state = this.states.LandingPage;
  device: PghUserDeviceBase;

  private registerDeviceInProgress = false;

  public imeiInfoDeviceType: ImeiInfoDeviceType = ImeiInfoDeviceType.Smartphone;
  public nextStepsInfoDeviceTypeTemplate: NextStepsInfoDeviceTemplate = NextStepsInfoDeviceTemplate.Smartphone;
  public imeiLookupDeviceTypeTemplate: ImeiLookupDeviceTemplate = ImeiLookupDeviceTemplate.Smartphone;

  constructor(
    private route: ActivatedRoute,
    private resourceService: ADPResourceService,
    private accountService: AccountService,
    private utilityService: UtilityService,
    private deviceInventoryService: DeviceInventoryService,
    private errorDialogService: GlobalModalWindowService,
  ) { }

  ngOnInit(): void {
    this.invitationCode = this.route?.snapshot?.params?.id;
  }

  loadDevice(): void {
    // If the code does not work, let the global error handler display the message.
    this.accountService.getDeviceFromInvitation(this.invitationCode).subscribe({
      next: (value) => {
        this.device = this.deviceInventoryService.buildPghUserDeviceBaseFromDeviceDto(value);
        // If device already has phone number and IMEI then go directly to Hyla
        if (this.device.imei && this.device.lineNumber) {
          sessionStorage.setItem('Disable_Device_Cache', 'true');
          this.registerDeviceInProgress = false;
          this.redirectToHyla(this.device.imei);
        } else {
          this.changeState(this.states.NextSteps);
        }
      },
      error: () => {
        this.errorDialogService.displaySimpleErrorModal('RegisterCellularDevice_Invitation_Invalid_Message');
        return;
      }
    });
  }

  changeState(state: any): void {
    if (state != this.states.LandingPage) {
      this.isBackVisible = true;
    }
    else {
      this.isBackVisible = false;
    }

    this.state = state;
  }

  gotoBack() {
    if (this.state == this.states.NextSteps) {
      this.state = this.states.LandingPage
      this.isBackVisible = false;
    }
    else if (this.state == this.states.ConfirmNumber) {
      this.state = this.states.NextSteps;
      this.isBackVisible = true;
    }
    else if (this.state == this.states.ImeiInfo) {
      this.state = this.states.ConfirmNumber;
      this.isBackVisible = true;
    }
    else if (this.state == this.states.ProvideAdditionalInfo) {
      this.state = this.states.ImeiInfo;
      this.isBackVisible = true;
    }
  }

  goToImeiLookup(e: any): void {
    this.phoneNumber = e.data[0].phoneNumber;
    this.device = e.data[0].device;
    this.changeState(e.state);
  }

  redirectToHyla(imei: string = null) {
    // If the user is on a desktop, show the error message.
    if (this.isDesktop()) {
      this.errorDialogService.displaySimpleErrorModal('RegisterCellularDevice_Hyla_Desktop_Message');
      return;
    }

    //need to encode the imei in base 64 for Hyla test
    const imeiBase64 = btoa(imei ?? this.imeiStepResult.imei);

    // We need to use the code=706849 static code approach for now. So for right now we will only inject the invitation code string into the "id" part of the string. -Anthony 11/27/2023
    //Link example: 'https://appconfiguat.cloud.hylamobile.com/hyladiag/?co=095939&id={{code}}&code=706849&lang=0&aux={{imeiBase64}}';
    const hylaUrl = this.resourceService.getPortalSetting("URL_Hyla_Phone").Value;

    this.hylaLink = hylaUrl.replace(/{{code}}/g, this.invitationCode);
    this.hylaLink = this.hylaLink.replace(/{{imeiBase64}}/g, imeiBase64);

    if (!this.invitationCode) {
      throw new Error('Code parameter not found');
    }

    // launch test
    window.location.href = this.hylaLink;
  }

  closeSideNav() {
    this.sidenav.close();
  }

  public handleImeiLookupCompleted(result: ImeiResult) {
    this.imeiStepResult = result;
    this.changeState(this.states.ProvideAdditionalInfo);
  }

  public handleDeviceInfoFormSubmitted(deviceFormResult: DeviceInfoFormResult) {
    if (this.registerDeviceInProgress) {
      return;
    }
    this.registerDeviceInProgress = true;

    const updateDeviceDto: UpdateDeviceBodyDto = {
      id: this.device.deviceId,
      catalogSource: 'ESS',
      inventoryId: this.device.inventoryId,
      deviceTypeCode: 'SMPH',

      lineNumber: this.utilityService.getPhoneNumberInE164(this.phoneNumber),

      diagnosticStatus: "PENDING",

      modelIdentifier: deviceFormResult.equipmentInfo?.ModelIdentifier,
      make: deviceFormResult.equipmentInfo?.Manufacturer,
      model: deviceFormResult.equipmentInfo?.MarketingName,
      description: deviceFormResult.equipmentInfo?.Description,

      imei: this.imeiStepResult.imei,
      carrier: deviceFormResult.equipmentInfo?.Attributes?.Carrier,
      color: deviceFormResult.equipmentInfo?.Attributes?.Color ?? deviceFormResult.equipmentInfo?.Attributes?.Material,
      storageSize: deviceFormResult.equipmentInfo?.Attributes?.StorageSize,

      nickName: deviceFormResult.deviceNickname,
    };
    // TODO: make a buildCreateDeviceBodyDtoFromSerialSearchEquipmentInfo(....) method and add it to the DeviceInventoryService similar to the buildCreateDeviceBodyDtoFromPghUserDeviceBase(...) method

    this.accountService.updateDeviceForInvitation(this.invitationCode, updateDeviceDto)
      .subscribe({
        next: (res) => {
          sessionStorage.setItem('Disable_Device_Cache', 'true');

          this.registerDeviceInProgress = false;

          this.redirectToHyla();
        },
        error: (error: HttpErrorResponse) => {
          this.registerDeviceInProgress = false;

          if (error?.error?.ErrorCode === "Duplicate_Device_Nickname") {
            this.errorDialogService.displaySimpleErrorModal('RegisterCellularDevice_SubmitError_NicknameAlreadyExists_Message');
          } else {
            throw error;
          }
        },
      });
  }


  isDesktop(): boolean {
    // Check the window dimensions
    const minWidthForDesktop = 1024;
    // You can adjust this value as needed
    const isWideScreen = window.innerWidth >= minWidthForDesktop;
    // Check the user agent string
    const userAgent = navigator.userAgent.toLowerCase();
    const isMobileDevice = /mobile|android|iphone|ipad|tablet|opera mini|iemobile/.test(userAgent);
    return isWideScreen && !isMobileDevice;
  }
} // Usage example if (DeviceChecker.isDesktop()) { console.log('User is on a desktop'); } else { console.log('User is not on a desktop');

